import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import moment from "moment";
import DownloadPreviewDialog from "./DownloadPreviewDialog";

const PINK = "#FCE4EC";
const YELLOW = "#FFF9C4";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 3) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: { fontWeight: 600 },
  tableCell: {
    padding: "10px 14px",
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #f0f0f0",
  },
  // materialCell: {
  //   color: "#ec6535",
  //   fontWeight: 500,
  // },
  numericCell: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  pillBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    borderRadius: 9999,
    padding: "6px 22px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#9E4D27", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#e0d2cb", color: "#fff" },
  },
  tableHeaderRow: {
    border: "3px solid #f0f0f0",
    "& .MuiTableCell-stickyHeader": {
      backgroundColor: "#F5F5F5",
      fontWeight: 600,
    },
  },
}));

const monthColumnsFromRange = (start, end) => {
  const list = [];
  let cur = moment(start, "YYYY-MM-DD").startOf("month");
  const last = moment(end, "YYYY-MM-DD").startOf("month");
  while (cur.isSameOrBefore(last, "month")) {
    list.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "month");
  }
  return list;
};

const groupRows = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = [r.cmoOrgID, r.sponsorOrgID, r.collabrixProductID].join("||");
    if (!map.has(key)) {
      map.set(key, {
        productCode: r.productCode,
        productDescription: r.productDescription,
        uom: r.uom,
        cells: {},
      });
    }
    map.get(key).cells[r.forecastMonth] = r;
  });
  return Array.from(map.values());
};

const cellStyle = (cell) => {
  if (!cell) return {};
  if (cell.exceedsLowLimit) return { backgroundColor: PINK };
  if (cell.exceedsHighLimit) return { backgroundColor: YELLOW };
  return {};
};

const fmtNum = (n) =>
  n == null || n === ""
    ? ""
    : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });

export default function ForecastHistoryTable({
  historyDetails,
  historyDetailsLoading,
  selectedHistoryId,
  filters,
}) {
  const classes = useStyles();
  const isCMO = localStorage.getItem("collabType") === "CMO";

  const monthCols = useMemo(
    () => monthColumnsFromRange(filters.startMonth, filters.endMonth),
    [filters.startMonth, filters.endMonth]
  );

  const grouped = useMemo(
    () => groupRows(historyDetails || []),
    [historyDetails]
  );

  const [previewOpen, setPreviewOpen] = useState(false);

  const onDownload = () => {
    if (!selectedHistoryId) return;
    setPreviewOpen(true);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>File Preview</Typography>
        <Button
          variant="contained"
          className={classes.pillBtn}
          onClick={onDownload}
          disabled={
            !selectedHistoryId ||
            historyDetailsLoading ||
            (historyDetails || []).length === 0
          }
        >
          Download excel
        </Button>
      </Box>

      {historyDetailsLoading ? (
        <Box className={classes.empty}>
          <CircularProgress size={24} />
        </Box>
      ) : grouped.length === 0 ? (
        <Box className={classes.empty}>
          <Typography variant="body2">No data for this version.</Typography>
        </Box>
      ) : (
        <TableContainer style={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader className={classes.tableHeaderRow}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell}>
                  Material Code / Description
                </TableCell>
                <TableCell className={classes.tableCell}>UoM</TableCell>
                {monthCols.map((m) => (
                  <TableCell
                    key={m}
                    className={`${classes.tableCell} ${classes.numericCell}`}
                  >
                    {moment(m, "YYYY-MM-DD").format("MMM-YYYY")}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((g) => (
                <TableRow key={g.productCode}>
                  <TableCell
                    className={`${classes.tableCell}`}
                  >
                    {g.productCode} / {g.productDescription}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{g.uom}</TableCell>
                  {monthCols.map((m) => {
                    const cell = g.cells[m];
                    return (
                      <TableCell
                        key={m}
                        className={`${classes.tableCell} ${classes.numericCell}`}
                        style={cellStyle(cell)}
                      >
                        {cell == null ? "" : fmtNum(cell.quantity)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DownloadPreviewDialog
        open={previewOpen}
        loading={historyDetailsLoading}
        rows={historyDetails || []}
        cmoLabel={isCMO ? (filters.sponsorLabel || "") : (filters.cmoLabel || "")}
        brand={filters.brand || ""}
        monthRange={{ start: filters.startMonth, end: filters.endMonth }}
        syncId={selectedHistoryId}
        columnHeader={isCMO ? "Sponsor" : "CMO"}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
}
